import React, {useEffect, useRef, useState} from 'react';
import {
    CheckBox,
    Flex,
    Icon,
    PriorityIcon,
    Text,
    ToggleSwitch
} from '@priority-software/priority-style-react';
import {isRTL} from '../../../Localization';
import styles from '../styles/tabViewItem.module.scss';
import {HoverPart, Item, RenameInput} from './Item';
import cx from 'classnames';
import {DraggableItem, DraggableProperties} from './DraggableItem';
import {ItemType} from '../types';
import {Droppable, Draggable} from 'react-beautiful-dnd';
import {MenuActions, getHideOption} from '../utils/submenu';
import {priority} from '@priority-software/priority-proto-api/lib';
import text from '../texts';
import {isFieldMandatory} from '../utils/crossAPI';
import texts from '../texts';
import {isInvalidTabDesign} from '../utils/tab';

interface TabViewItemProps extends DraggableProperties {
    index: number;
    tab: ItemType;
    data: ItemType[];
    selectedItems: ItemType[];
    setSelectedItems: (items: ItemType[]) => void;
    isExpended: boolean;
    toggleTabExpended: (index: number) => void;
    foundItemName: string;
    viewItemName: string;
    activeTab: number;
    setActiveTab: (tabIndex: number) => void;
    moveItemBetweenTabs?: (
        sourceTabIndex: number,
        targetTabIndex: number,
        item: priority.netitems.form.IRowViewColumnsDesign
    ) => void;
    reorderTab: (startIndex: number, endIndex: number, tabIndex: number) => void;
    onUpdate: () => void;
    pic: priority.netitems.form.Picture;
    setPic: (pic: priority.netitems.form.Picture) => void;
    packages: priority.netitems.ngen.IPackage[];
    setPackages: (packages: priority.netitems.ngen.IPackage[]) => void;
}

export const TabViewItem = (props: TabViewItemProps) => {
    const {
        index,
        tab,
        data,
        pic,
        setPic,
        selectedItems,
        setSelectedItems,
        toggleTabExpended,
        isExpended,
        foundItemName,
        viewItemName,
        activeTab,
        setActiveTab,
        moveItemBetweenTabs,
        onUpdate,
        reorderTab,
        packages,
        setPackages
    } = props;
    const isTabNew = () => {
        return (
            !tab.Title?.length &&
            !(tab as priority.netitems.form.RowViewTabsDesign).ColumnsDesign?.length
        );
    };

    const isMainTab = () => {
        return index === 0;
    };

    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(isTabNew());

    const itemRef = useRef(null);

    useEffect(() => {
        if (isTabNew()) {
            //scrollIntoViewIfNeeded is not supported in Firefox
            itemRef.current?.scrollIntoViewIfNeeded
                ? itemRef.current?.scrollIntoViewIfNeeded()
                : itemRef.current?.scrollIntoView();
        }
    }, [tab]);

    useEffect(() => {
        if (
            (tab as priority.netitems.form.RowViewTabsDesign).ColumnsDesign.some(i =>
                viewItemName.includes(i.Name)
            ) &&
            !isExpended
        ) {
            toggleExpend();
        }
    }, [viewItemName, tab]);

    const isChecked = () => {
        return (
            getTabItems().length &&
            getTabItems().every(i => selectedItems.find(selected => i.Name === selected.Name))
        );
    };

    const isTabHidden = () => {
        return (
            getTabItems().length &&
            getTabItems().every(i => i.Hidden !== priority.netitems.form.Hide.Visible)
        );
    };

    const onCheckboxChange = () => {
        if (isChecked()) {
            setSelectedItems(
                selectedItems.filter(selected => !getTabItems().find(i => i.Name === selected.Name))
            );
        } else {
            setSelectedItems?.([
                ...selectedItems,
                ...getTabItems().filter(
                    i => !selectedItems.find(selected => i.Name === selected.Name)
                )
            ]);
        }
    };

    const toggleExpend = () => {
        if (!isTabNew()) {
            toggleTabExpended(index);
        }
    };

    const getTabItems = (): priority.netitems.form.IRowViewColumnsDesign[] => {
        return (tab as priority.netitems.form.RowViewTabsDesign).ColumnsDesign;
    };

    const onItemSelect = (items: ItemType[]) => {
        if (activeTab != index) {
            setActiveTab(index);
        }
        setSelectedItems(items);
    };

    const onShowHide = () => {
        const hidden = isTabHidden();
        if (!hidden && isChecked()) {
            onCheckboxChange();
        }
        getTabItems().map(i => {
            const newHiddenStatus = hidden
                ? priority.netitems.form.Hide.Visible
                : priority.netitems.form.Hide.HiddenByDesign;
            if (!isFieldMandatory(i.Name)) {
                i.Hidden = newHiddenStatus;
            }
            if ((i as any).merged) {
                (i as any).merged.Hidden = hidden
                    ? priority.netitems.form.Hide.Visible
                    : priority.netitems.form.Hide.HiddenByDesign;
            }
        });
        onUpdate();
    };

    const renderItem = props => {
        const {item, parentTabIndex, index} = props;
        let menuData = [];
        const isMandatory: boolean = isFieldMandatory(item.Name);
        if (!item.Hidden) {
            const moveToSubmenu =
                data.length > 1 &&
                data
                    .filter((_, idx) => idx !== parentTabIndex)
                    .map((tab, idx) => {
                        return {
                            title: (tab as priority.netitems.form.RowViewTabsDesign).Title,
                            onClick: () =>
                                moveItemBetweenTabs(
                                    parentTabIndex,
                                    idx < parentTabIndex ? idx : idx + 1,
                                    item
                                )
                        };
                    });
            const tabItems = (data[parentTabIndex] as priority.netitems.form.RowViewTabsDesign)
                .ColumnsDesign;
            const mergeSubmenu = tabItems
                .filter(c => !c.Merge && c.Name !== item.Name)
                .map(i => {
                    return {
                        title: i.Title,
                        onClick: () => {
                            item.Merge = true;
                            const startInd = tabItems.indexOf(i);
                            reorderTab(startInd, props.index + 1, parentTabIndex);
                        }
                    };
                });
            menuData = [
                {
                    title: item.Merge ? text.split : text.merge,
                    subMenu: item.Merge ? null : mergeSubmenu,
                    action: item.Merge ? MenuActions.MERGE : null
                },
                {title: text.rename, action: MenuActions.RENAME}
            ];
            index !== 0 &&
                menuData.push({
                    title: item.Newarea ? text.removeSpace : text.addSpace,
                    action: MenuActions.SPACE
                });
            data.length > 1 && menuData.unshift({title: text.moveTo, subMenu: moveToSubmenu});
        }
        const pack = packages.find(p => p?.PackageId === item.PackageId);
        const hideOption = !isMandatory && getHideOption(item, pack);
        hideOption && menuData.unshift(hideOption);
        return (
            <DraggableItem {...props} key={item?.Name}>
                <Item
                    componentClasses={styles.groupItem}
                    item={item}
                    pack={packages.find(p => p.PackageId === item.PackageId)}
                    packages={packages}
                    setPackages={setPackages}
                    setSelectedItems={onItemSelect}
                    selectedItems={selectedItems}
                    isFound={
                        foundItemName === item.Name ||
                        (item.merged && foundItemName === item.merged.Name)
                    }
                    isViewItem={viewItemName.includes(item.Name)}
                    menuData={menuData}
                    data={data}
                    onUpdate={onUpdate}
                    isMandatory={isMandatory}
                    isInvalidDesign={isInvalidTabDesign(
                        (tab as priority.netitems.form.RowViewTabsDesign).ColumnsDesign,
                        index,
                        parentTabIndex
                    )}
                    {...props}
                />
            </DraggableItem>
        );
    };

    const menuActionHandler = (action: MenuActions) => {
        if (action === MenuActions.RENAME) {
            setIsEditing(true);
        }
    };

    const closeHandler = () => {
        if (isTabNew()) {
            data.splice(index, 1);
            onUpdate();
        } else {
            setIsEditing(false);
        }
    };

    const togglePic = () => {
        setPic(
            pic === priority.netitems.form.Picture.HiddenPicture
                ? priority.netitems.form.Picture.VisiblePicture
                : priority.netitems.form.Picture.HiddenPicture
        );
    };

    const isInvalidDesignInside = getTabItems().some((item, i) =>
        isInvalidTabDesign(
            (tab as priority.netitems.form.RowViewTabsDesign).ColumnsDesign,
            i,
            index
        )
    );

    return (
        <div ref={itemRef} onMouseLeave={() => setIsMenuOpen(false)}>
            <Flex
                componentClasses={cx(styles.item, {[styles.expandedWrapper]: activeTab === index})}
                direction="column"
            >
                {
                    <Droppable key={index} droppableId={'inner-' + index} type="ITEM">
                        {provided => (
                            <div ref={provided.innerRef} className={styles.list}>
                                <Flex
                                    justifyContent="flex-start"
                                    componentClasses={cx(styles.tab, {
                                        [styles.expandedTab]: isExpended,
                                        [styles.hiddenItem]: isTabHidden(),
                                        [styles.mainTab]: isMainTab()
                                    })}
                                >
                                    <Flex justifyContent="flex-start">
                                        <div
                                            {...props.dragHandleProps}
                                            className={cx(styles.dragIconContainer, {
                                                [styles.draggedIconContainer]:
                                                    props.isDragged || isExpended
                                            })}
                                        >
                                            <Icon icon={PriorityIcon.reorder} skin="standart" />
                                        </div>
                                        <CheckBox
                                            checked={isChecked()}
                                            onChange={onCheckboxChange}
                                            componentClasses={styles.checkbox}
                                            disabled={isTabHidden() || isTabNew() || isMainTab()}
                                        />
                                        <Icon
                                            icon={
                                                isRTL()
                                                    ? PriorityIcon.arrowLeft
                                                    : PriorityIcon.arrowRight
                                            }
                                            iconColor="secondary-400"
                                            componentClasses={cx(styles.arrow, {
                                                [styles.arrowExpended]: isExpended
                                            })}
                                            onClick={toggleExpend}
                                        />
                                        {isEditing ? (
                                            <RenameInput
                                                onClose={closeHandler}
                                                onUpdate={onUpdate}
                                                item={tab}
                                                placeholder={text.enterTabName}
                                            />
                                        ) : (
                                            <Text skin="paragraph-2">
                                                {
                                                    (
                                                        tab as priority.netitems.form.RowViewTabsDesign
                                                    ).Title
                                                }
                                            </Text>
                                        )}
                                    </Flex>
                                    {!isEditing && !isMainTab() && (
                                        <HoverPart
                                            componentClasses={styles.hoverPart}
                                            menuData={
                                                !isTabHidden() && [
                                                    {title: text.rename, action: MenuActions.RENAME}
                                                ]
                                            }
                                            isHidden={isTabHidden()}
                                            onShowHide={onShowHide}
                                            setIsMenuOpen={setIsMenuOpen}
                                            isMenuOpen={isMenuOpen}
                                            menuActionHandler={menuActionHandler}
                                        />
                                    )}
                                    {isInvalidDesignInside && (
                                        <Icon icon={PriorityIcon.attention} />
                                    )}
                                </Flex>
                                {isExpended && (
                                    <>
                                        <div className={styles.innerList}>
                                            {getTabItems().map((item, idx) => {
                                                const key = 'tab-' + index + '-' + idx;
                                                return (
                                                    <Draggable
                                                        key={key}
                                                        draggableId={`item-${key}`}
                                                        index={idx}
                                                        type="ITEM"
                                                    >
                                                        {(provided, snapshot) => {
                                                            return renderItem({
                                                                item: item,
                                                                index: idx,
                                                                parentTabIndex: index,
                                                                isLastIndex:
                                                                    idx === data.length - 1,
                                                                key: key,
                                                                ...provided,
                                                                isDragged: snapshot.isDragging,
                                                                inDrag: snapshot.isDragging
                                                            });
                                                        }}
                                                    </Draggable>
                                                );
                                            })}
                                        </div>
                                        {index === 0 &&
                                            pic !== priority.netitems.form.Picture.NoPicture && (
                                                <Flex
                                                    justifyContent="flex-start"
                                                    componentClasses={styles.togglePicContainer}
                                                >
                                                    <ToggleSwitch
                                                        checked={
                                                            pic ===
                                                            priority.netitems.form.Picture
                                                                .VisiblePicture
                                                        }
                                                        onChange={togglePic}
                                                        componentClasses={styles.togglePic}
                                                    />
                                                    <Text skin="paragraph-2" startPadding>
                                                        {texts.showImage}
                                                    </Text>
                                                </Flex>
                                            )}
                                    </>
                                )}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                }
            </Flex>
        </div>
    );
};
